import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { UserSettingsStateService } from '@services/user-settings.state.service';
import { API_URL, APP_CONFIG, ConfigSettings, THEMES } from '@shared/config';
import { defaultTheme } from '@shared/config/themes';
import { Branding } from '@shared/models';
import {
  UserSettingsPatch,
  UserSettingsSpeech,
} from '@shared/models/user-settings.model';
import { BrandingService } from '@shared/services/branding.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, map, mapTo, switchMap, tap } from 'rxjs/operators';
import { VoiceRecognitionService } from './voice-recognition.service';

@Injectable()
export class OrUserSettingsService {
  globalSettings: ConfigSettings;
  private readonly url: string;
  private themeLoading = new BehaviorSubject(false);
  public themeLoading$ = this.themeLoading.asObservable();

  constructor(
    @Inject(APP_CONFIG) settings: ConfigSettings,
    private brandingService: BrandingService,
    private http: HttpClient,
    private voiceRecognitionService: VoiceRecognitionService,
    private userSettingsStateService: UserSettingsStateService
  ) {
    this.globalSettings = settings;
    this.url = `${this.globalSettings.orApiUrl}${API_URL.userSettings}`;
  }

  getMyUserSettings(): Observable<UserSettingsSpeech> {
    return this.http.get<UserSettingsSpeech>(`${this.url}`, {
      withCredentials: true,
    });
  }

  private patchUserSettings(
    userSettings: UserSettingsPatch
  ): Observable<UserSettingsSpeech> {
    return this.http.patch<UserSettingsSpeech>(this.url, userSettings, {
      withCredentials: true,
    });
  }

  public changeTheme(theme: THEMES): Observable<Branding> {
    this.isThemeLoading(true);
    const userSettingsId$ = this.userSettingsStateService.userSettingsId$;
    return userSettingsId$.pipe(
      switchMap(userSettingsId =>
        this.patchUserSettings({ userSettingsId: userSettingsId, theme })
      ),
      switchMap(() => this.brandingService.getBrandingByThemeName(theme)),
      tap((branding: Branding) => {
        this.brandingService.loadThemeValues(branding);
        this.isThemeLoading(false);
      })
    );
  }

  private isThemeLoading(themeLoading: boolean): void {
    this.themeLoading.next(themeLoading);
  }

  public loadSpeechSettings(): Observable<UserSettingsSpeech> {
    if (!LocalStorageService.isLoggedIn()) {
      return;
    }

    return this.getMyUserSettings().pipe(
      tap(({ speechActive }) => this.loadSpeech(speechActive))
    );
  }

  public changeSpeech(speechActive: boolean): Observable<UserSettingsSpeech> {
    const userSettings$ = this.userSettingsStateService
      .getUserSettings()
      .pipe(first());
    return userSettings$.pipe(
      map(({ userSettingsId }) => ({ userSettingsId, speechActive })),
      switchMap(userSettings => this.patchUserSettings(userSettings)),
      tap(() => this.loadSpeech(speechActive))
    );
  }

  private loadSpeech(speechActive: boolean): void {
    speechActive
      ? this.voiceRecognitionService.start()
      : this.voiceRecognitionService.stop();
  }

  public loadBrandingSettings(): Observable<UserSettingsSpeech> {
    if (!LocalStorageService.isLoggedIn()) {
      return;
    }

    return this.getMyUserSettings().pipe(
      switchMap((userSettings: UserSettingsSpeech) => {
        return this.loadBranding(userSettings);
      })
    );
  }

  private loadBranding(
    userSettings: UserSettingsSpeech
  ): Observable<UserSettingsSpeech> {
    const { userSettingsId, theme } = userSettings;
    return this.brandingService.getBrandingByThemeName(theme).pipe(
      tap((branding: Branding) => {
        this.brandingService.loadThemeValues(branding, userSettingsId);
      }),
      mapTo(userSettings)
    );
  }

  unsetUserSettings(): void {
    this.userSettingsStateService.setUserSettingsId(null);
    this.userSettingsStateService.setTheme(defaultTheme);
    this.userSettingsStateService.setUserSettingsId(null);
  }
}
